'use client';

import { SecurityGroupRuleProtocol } from '@repo/api-gw-sdk';
import type { PrivateLink } from '@repo/api-gw-sdk';

import { Wizard } from '@/components/wizard/Wizard';

import { AddPrivateLinkCreationFlow } from './AddPrivateLinkCreationFlow';
import {
  defaultAddPrivateLinkWizardState,
  type AddPrivateLinkWizardState,
  type DisplayProtocol,
} from './addPrivateLinkWizardState';

interface AddPrivateLinkWizardPanelProps {
  workspaceId: string;
  workspaceRegion: string;
  editingPrivateLink?: PrivateLink;
  onAbort: () => void;
  onCreated: () => void;
}

export function AddPrivateLinkWizardPanel({
  workspaceId,
  workspaceRegion,
  editingPrivateLink,
  onAbort,
  onCreated,
}: AddPrivateLinkWizardPanelProps) {
  const isEditing = !!editingPrivateLink;

  const initialState: AddPrivateLinkWizardState = isEditing
    ? {
        workspaceId,
        workspaceRegion,
        serviceName: editingPrivateLink.serviceName,
        region: editingPrivateLink.region,
        fqdn: editingPrivateLink.fqdn,
        securityGroupRules: (editingPrivateLink.securityGroupRules ?? []).map(
          (r) => {
            let displayProtocol: DisplayProtocol;
            if (
              r.protocol ===
              SecurityGroupRuleProtocol.SECURITY_GROUP_RULE_PROTOCOL_ALL
            ) {
              displayProtocol = 'ALL_TRAFFIC';
            } else if (
              r.protocol ===
                SecurityGroupRuleProtocol.SECURITY_GROUP_RULE_PROTOCOL_TCP &&
              r.fromPort === 0 &&
              r.toPort === 65535
            ) {
              displayProtocol = 'ALL_TCP';
            } else if (
              r.protocol ===
                SecurityGroupRuleProtocol.SECURITY_GROUP_RULE_PROTOCOL_UDP &&
              r.fromPort === 0 &&
              r.toPort === 65535
            ) {
              displayProtocol = 'ALL_UDP';
            } else {
              displayProtocol =
                r.protocol ===
                SecurityGroupRuleProtocol.SECURITY_GROUP_RULE_PROTOCOL_UDP
                  ? 'UDP'
                  : 'TCP';
            }
            return {
              protocol: r.protocol,
              fromPort: r.fromPort,
              toPort: r.toPort,
              displayProtocol,
            };
          }
        ),
        mode: 'edit',
        editingPrivateLinkId: editingPrivateLink.id,
      }
    : {
        ...defaultAddPrivateLinkWizardState,
        workspaceId,
        workspaceRegion,
        region: workspaceRegion,
      };

  return (
    <Wizard<AddPrivateLinkWizardState>
      onAbort={onAbort}
      hideBackArrow={true}
      onFinish={async () => {
        onCreated();
        return true;
      }}
      flow={[AddPrivateLinkCreationFlow]}
      initialState={initialState}
      title={
        isEditing ? 'Edit PrivateLink Endpoint' : 'Add PrivateLink Endpoint'
      }
      stepperLabels={[]}
    />
  );
}
